import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface FormPageSkeletonProps {
  /** 是否绘制右侧预览表格列（新建广告为 `200px / 1fr / 300px` 三列布局） */
  withPreviewColumn?: boolean;
  /** 左侧锚点菜单占位项数 */
  anchorCount?: number;
  /** 中间表单配置段数量 */
  sectionCount?: number;
  /** 每个配置段内的字段行数 */
  fieldCount?: number;
  /** 底部操作栏按钮数量 */
  actionCount?: number;
  className?: string;
}

/** 表单配置段内的一行字段：左侧 label（w-25）+ 右侧内容区（w-135），对齐 FormRenderer 的横向布局 */
function FieldRow() {
  return (
    <div className="flex flex-row items-start gap-4">
      <Skeleton className="h-6 w-25 shrink-0" />
      <Skeleton className="h-9 w-full max-w-135" />
    </div>
  );
}

/** 表单配置段：对齐 AdConfigForm 的 SectionCard（Card + 标题栏 + 字段区） */
function SectionSkeleton({ fieldCount }: { fieldCount: number }) {
  return (
    <Card className="gap-0">
      <CardHeader>
        <div className="flex flex-row items-center gap-2">
          <Skeleton className="size-4 shrink-0 rounded" />
          <Skeleton className="h-5 w-28" />
          <Skeleton className="ml-auto h-5 w-12" />
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {Array.from({ length: fieldCount }, (_, index) => (
          <FieldRow key={index} />
        ))}
      </CardContent>
    </Card>
  );
}

/** 左侧锚点菜单占位：对齐 AnchorMenu 的 10px 圆点 + ml-20px / h-30px 文字块 */
function AnchorMenuSkeleton({ count }: { count: number }) {
  return (
    <ul className="relative flex flex-col gap-2">
      {Array.from({ length: count }, (_, index) => (
        <li key={index} className="relative flex items-center">
          <Skeleton className="size-2.5 shrink-0 rounded-full" />
          <Skeleton className="ml-5 h-[30px] flex-1 rounded-[4px]" />
        </li>
      ))}
    </ul>
  );
}

/** 右侧预览表格占位（新建广告页的第三列） */
function PreviewColumnSkeleton() {
  return (
    <div className="flex flex-col gap-2 rounded-xl border bg-card p-3">
      <Skeleton className="h-8 w-full" />
      <div className="flex items-center gap-3 border-y bg-muted px-3" style={{ height: 40 }}>
        {/* 本主题 --muted 与 --accent 同色，列占位块必须显式给对比色才看得见 */}
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-3.5 flex-1 bg-[#e4e4e4] dark:bg-[#3a3a3a]" />
        ))}
      </div>
      {Array.from({ length: 5 }, (_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-3 border-b px-3" style={{ height: 44 }}>
          {Array.from({ length: 3 }, (_, columnIndex) => (
            <Skeleton key={columnIndex} className="h-3.5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * 表单编辑页骨架：左侧锚点菜单 + 中间表单配置段（+ 可选右侧预览表格）+ 底部操作栏。
 *
 * 结构对齐 AddADV2 / EditDraft / EditStrategyTemplate 三页共用的布局
 * （`grid-cols-[200px_1fr]` 或 `grid-cols-[200px_1fr_300px]` + `h-10` 底部操作栏）。
 */
export function FormPageSkeleton({
  withPreviewColumn = false,
  anchorCount = 6,
  sectionCount = 4,
  fieldCount = 3,
  actionCount = 4,
  className,
}: FormPageSkeletonProps) {
  return (
    <div
      data-testid="form-page-skeleton"
      className={cn('w-full h-full relative flex flex-col overflow-y-hidden', className)}
    >
      <div
        data-testid="form-page-skeleton-grid"
        className={cn(
          'flex-1 grid gap-4 w-full overflow-y-auto',
          withPreviewColumn ? 'grid-cols-[200px_1fr_300px]' : 'grid-cols-[200px_1fr]'
        )}
      >
        {/* 左侧锚点菜单 */}
        <div data-testid="form-page-skeleton-anchor" className="sticky top-0 h-fit">
          <AnchorMenuSkeleton count={anchorCount} />
        </div>

        {/* 中间表单配置区域 */}
        <div data-testid="form-page-skeleton-form" className="min-w-0 flex flex-col gap-4 pb-4">
          {Array.from({ length: sectionCount }, (_, index) => (
            <SectionSkeleton key={index} fieldCount={fieldCount} />
          ))}
        </div>

        {/* 右侧预览表格 */}
        {withPreviewColumn && (
          <div data-testid="form-page-skeleton-preview" className="sticky top-0 h-fit">
            <PreviewColumnSkeleton />
          </div>
        )}
      </div>

      {/* 底部操作栏 */}
      <div className="w-full h-10 shrink-0 border-t bg-background/95 backdrop-blur flex flex-row items-center gap-4">
        <div className="ml-auto flex flex-row items-center gap-4">
          {Array.from({ length: actionCount }, (_, index) => (
            <Skeleton key={index} className="h-8 w-24" />
          ))}
        </div>
      </div>
    </div>
  );
}
